import { Box } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { CategoryTypeEnum } from 'entities/Files/types';
import { useDebounce } from 'shared/helpers/useDebounce';
import { SearchParams } from 'shared/helpers/useFilesSearchParams';
import { Button } from 'shared/ui/Button';
import { DateRangeInput } from 'shared/ui/DateRangePicker';
import { SearchInput } from 'shared/ui/SearchInput';
import { Select } from 'shared/ui/Select';
import { SelectOption } from 'shared/ui/Select/types';
import { TabButton } from 'shared/ui/TabButton';
import { FilterDrawer } from 'shared/ui/FilterDrawer';
import { SavedFileStatus } from 'entities/Files/types';
import { OptionsDropdown } from 'shared/ui/OptionsDropdown';
import { Routes } from 'app/config/routes/consts';
import { useModelsOptions } from 'shared/hooks/use-models-options';

interface Props {
	category?: string;
	filesCounters?: { unrecognized: number; recognized: number; inProcess: number };
	searchParams: SearchParams;
	changeSearchParams: (searchParams: Partial<SearchParams>) => void;
	handleToggleAllFiles: (refresh?: boolean) => void;
	uploaderOptions: Array<SelectOption>;
}

export const FiltersAndSearch: FC<Props> = ({
	category,
	filesCounters,
	searchParams,
	changeSearchParams,
	handleToggleAllFiles,
	uploaderOptions,
}) => {
	const navigate = useNavigate();
	const { modelsOptions, getModelType } = useModelsOptions();
	const { partnerId } = useParams();
	const { t } = useTranslation();

	const [filterMenu, setFilterMenu] = useState<boolean>(false);

	const handleCloseFilterMenu = () => setFilterMenu(false);

	const [searchInputValue, setSearchInputValue] = useState<string>(searchParams.search);
	const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchInputValue(event.target.value);
	};
	const debouncedSearchInputValue = useDebounce<string>(searchInputValue, 500);
	useEffect(() => {
		handleToggleAllFiles(true);
		changeSearchParams({ search: debouncedSearchInputValue });
	}, [debouncedSearchInputValue]);

	const [sortFilter, setSortFilter] = useState<SearchParams['sort']>(searchParams.sort);
	const [uploadSourceFilter, setUploadSourceFilter] = useState<SearchParams['uploadSource']>(
		searchParams.uploadSource,
	);
	const [modelsFilter, setModelsFilter] = useState<SearchParams['models']>(searchParams.models);
	const [uploaderIdsFilter, setUploaderIdsFilter] = useState<SearchParams['uploaderIdsFilter']>(
		searchParams.uploaderIdsFilter,
	);
	const [importRange, setImportRange] = useState<{
		from?: SearchParams['importFrom'];
		to?: SearchParams['importTo'];
	}>({
		from: searchParams.importFrom,
		to: searchParams.importFrom,
	});
	const [autoRemoveRange, setAutoRemoveRange] = useState<{
		from?: SearchParams['autoRemoveFrom'];
		to?: SearchParams['autoRemoveTo'];
	}>({
		from: searchParams.autoRemoveFrom,
		to: searchParams.autoRemoveTo,
	});

	const modelValues = useMemo(
		() => modelsFilter.map((item) => item.generativeModelId || item.customModelId || item.category),
		[modelsFilter],
	);

	const handleOpenFilterMenu = () => {
		setFilterMenu(true);
	};

	const applyFilters = () => {
		handleToggleAllFiles(true);
		changeSearchParams({
			sort: sortFilter,
			uploadSource: uploadSourceFilter,
			models: modelsFilter,
			uploaderIdsFilter: uploaderIdsFilter,
			importFrom: importRange.from,
			importTo: importRange.to,
			autoRemoveFrom: autoRemoveRange.from,
			autoRemoveTo: autoRemoveRange.to,
		});
	};

	const switchTabHandler = async (status: SavedFileStatus) => {
		handleToggleAllFiles(true);
		changeSearchParams({ status });
	};

	return (
		<Box sx={{ display: 'flex', flexDirection: 'row', gap: '20px', marginBottom: '30px' }}>
			<TabButton
				label={`${t('files.tabs.tab_1')} (${filesCounters?.unrecognized ?? '0'})`}
				isActive={searchParams.status === 'UNRECOGNIZED'}
				action={() => switchTabHandler('UNRECOGNIZED')}
			/>
			<TabButton
				label={`${t('files.tabs.tab_2')} (${filesCounters?.inProcess ?? '0'})`}
				isActive={searchParams.status === 'IN_PROCESS'}
				action={() => switchTabHandler('IN_PROCESS')}
			/>
			<TabButton
				label={`${t('files.tabs.tab_3')} (${filesCounters?.recognized ?? '0'})`}
				isActive={searchParams.status === 'RECOGNIZED'}
				action={() => switchTabHandler('RECOGNIZED')}
			/>
			{!partnerId && (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						width: '100%',
						gap: '20px',
					}}
				>
					<SearchInput value={searchInputValue} onChange={handleSearchInputChange} />
					<FilterDrawer
						isOpen={filterMenu}
						handleOpen={handleOpenFilterMenu}
						handleClose={handleCloseFilterMenu}
					>
						<Select
							label={t('files.labels.order') as string}
							value={sortFilter}
							placeholder={t('files.placeholder.order')}
							onSelect={(value) => setSortFilter(value as SearchParams['sort'])}
							options={SortSelectOptions}
						/>
						{category === 'all' && (
							<Select
								label={t('files.labels.category') as string}
								value={modelValues as string[]}
								placeholder={t('files.placeholder.category')}
								onSelect={(value) => {
									if (value) {
										const models = (value as string[])?.map((item) => ({
											category: getModelType(item) as CategoryTypeEnum,
											customModelId:
												getModelType(item) === CategoryTypeEnum.custom
													? item
													: undefined,
											generativeModelId:
												getModelType(item) === CategoryTypeEnum.generative_model
													? item
													: undefined,
										}));
										setModelsFilter(models);
									}
								}}
								options={modelsOptions}
								multiple={true}
							/>
						)}
						<DateRangeInput
							name={'By the importing date/period'}
							label={t('files.labels.import_date')}
							placeholder={t('shared_texts.placeholder.date') as string}
							startDate={importRange.from}
							endDate={importRange.to}
							changeRange={(startDate, endDate) => {
								setImportRange((prevState) => ({
									...prevState,
									from: startDate,
									to: endDate,
								}));
							}}
						/>
						<Select
							label={t('files.labels.import_option') as string}
							value={uploadSourceFilter}
							placeholder={t('files.placeholder.import_option')}
							onSelect={(value) => setUploadSourceFilter(value as SearchParams['uploadSource'])}
							options={UploadSourceFilterSelectOptions}
						/>
						<DateRangeInput
							name={'Period of automatic removal'}
							label={t('files.labels.removal_date')}
							placeholder={t('shared_texts.placeholder.date') as string}
							startDate={autoRemoveRange.from}
							endDate={autoRemoveRange.to}
							changeRange={(startDate, endDate) => {
								setAutoRemoveRange((prevState) => ({
									...prevState,
									from: startDate,
									to: endDate,
								}));
							}}
						/>
						<Select
							label={t('files.labels.import_user') as string}
							value={uploaderIdsFilter}
							placeholder={t('files.placeholder.import_user')}
							onSelect={(value) => setUploaderIdsFilter(value as string[])}
							options={uploaderOptions}
							multiple={true}
						/>
						<Button
							onClick={() => {
								applyFilters();
								handleCloseFilterMenu();
							}}
							variant={'contained'}
							fullWidth
						>
							{t('shared_texts.apply_filters')}
						</Button>
					</FilterDrawer>
					<OptionsDropdown
						iconId={'setting_line_fill'}
						optionList={[
							{
								label: t('files.select_options.manage_export'),
								value: 'manage_export',
								action: () => navigate(Routes.filesSettings),
							},
						]}
					/>
				</Box>
			)}
		</Box>
	);
};

const SortSelectOptions: Array<SelectOption> = [
	{
		value: 'asc',
		label: 'files.select_options.by_the_oldest_the_newest',
	},
	{
		value: 'desc',
		label: 'files.select_options.by_the_newest_the_oldest',
	},
];

const UploadSourceFilterSelectOptions: Array<SelectOption> = [
	{
		value: 'all',
		label: 'shared_texts.all',
	},
	{
		value: 'DIRECT',
		label: 'shared_texts.direct',
	},
	{
		value: 'FTP',
		label: 'FTP',
	},
	{
		value: 'EMAIL',
		label: 'Email',
	},
];
